import { useRef } from "react";
import { Link } from "react-router-dom";
import { useBackload, type BackloadReport } from "../../api/admin";
import { StatusChip } from "../../components/ui";
import AdminLayout from "./AdminLayout";

/** Back-load the buyer's consolidated quotes workbook; every held row says
 * why. Loading is idempotent -- the same file twice changes nothing -- so
 * a report that went wrong can be fixed in the workbook and simply run again. */
export function BackloadPage() {
  const backload = useBackload();
  const input = useRef<HTMLInputElement>(null);
  const report = backload.data;
  return (
    <AdminLayout
      title="Back-load quotes"
      sub="Bring in quotations from the buyer's consolidated workbook"
      actions={
        <button type="button" className="btn btn-primary" disabled={backload.isPending} onClick={() => input.current?.click()}>
          {backload.isPending ? "Reading…" : "Choose workbook (.xlsm)"}
        </button>
      }
    >
      <input
        ref={input}
        type="file"
        accept=".xlsm,.xlsx"
        hidden
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) {
            backload.mutate(file);
          }
          e.target.value = "";
        }}
      />
      {backload.isError ? <p className="notice bad">{backload.error.message}</p> : null}
      {report ? (
        <BackloadResults report={report} />
      ) : (
        <div className="dashed-box backload-empty">
          Upload the .xlsm the buyer keeps. Quotes that place on one site load; the rest are held with the reason.
          Uploading the same file again changes nothing.
        </div>
      )}
    </AdminLayout>
  );
}

function BackloadResults({ report }: { report: BackloadReport }) {
  return (
    <>
      <p className="notice ok">
        {report.filename}: {report.quotes_seen} quotes and {report.lines_seen} lines read.
      </p>
      {report.errors.length > 0 ? (
        <ul className="notice bad history-list">
          {report.errors.map((err) => (
            <li key={err}>{err}</li>
          ))}
        </ul>
      ) : null}
      <div className="table-card">
        <table className="data-table">
          <thead>
            <tr>
              <th>Quote</th>
              <th>Outcome</th>
              <th>Site</th>
              <th>Why / candidates</th>
            </tr>
          </thead>
          <tbody>
            {report.results.map((r) => (
              <tr key={r.quote_ref}>
                <td className="mono">
                  {r.quotation_id ? <Link to={`/quotations/${r.quotation_id}`}>{r.quote_ref}</Link> : r.quote_ref}
                </td>
                <td>
                  <StatusChip tone={r.outcome === "loaded" ? "ok" : "warn"}>{r.outcome.replace(/_/g, " ")}</StatusChip>
                </td>
                <td>{r.site_id ? <Link to={`/sites/${r.site_id}`}>{r.site_name ?? `Site ${r.site_id}`}</Link> : "—"}</td>
                <td>
                  {r.reason ?? ""}
                  {r.candidates.length > 0 ? (
                    <ul className="history-list">
                      {r.candidates.map((c) => (
                        <li key={c.id}>
                          <Link to={`/sites/${c.id}`}>{c.name}</Link>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
